/**
 * The scene studio (spec 029): the chat-first view of one standalone scene,
 * for Maya. Two areas side by side:
 *
 * - the chat: 022's scene conversation (`AssistantPanel`, the same component
 *   and conversation as the hood's Assistant tab);
 * - the result: the scene in spec 009's `PlayerHost`, paused on the frame at
 *   0 until played, under 009's `PreviewTransport` (D-14; the scene as
 *   the scene store holds it, unsaved edits included) and 025's direct-edit
 *   strip under it, filling through the scene's own picture map (D-9, spec
 *   030's scene picture owner, mounted by the standalone `SceneEditor`);
 *   after the turn that answered a pending scene brief, 026's handoff in
 *   place of the preview (D-13).
 *
 * It reads and writes the same `sceneStore` and `assistantStore` as Under the
 * hood, so switching views loses nothing; it changes no runtime code.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Orientation, Story } from '../../../../Shared/src/@types';
import type { PlayableSceneInput } from '../../../../Shared/src/runtime/playableScene';
import { previewSceneInput, useSceneStore } from '../../stores/sceneStore';
import { isEditingTarget, isModalOpen } from '../../hooks/useSceneShortcuts';
import { PlayerHost, type PlayerHostHandle } from '../preview/PlayerHost';
import OrientationButtons from '../preview/OrientationButtons';
import { PreviewTransport } from '../preview/PreviewTransport';
import AssistantPanel from '../motion/assistant/AssistantPanel';
import { momentOf, useAssistantView } from '../assistant/assistantView';
import { useAssistantStore, type AssistantMessage } from '../../stores/assistantStore';
import { pictureMapCounts } from '../../../../Shared/src/pictures';
import PicturesHandoff, { closeHandoff, isHandoffClosed, sceneHandoffKey } from '../pictures/PicturesHandoff';
import { PictureOwnerContext } from '../pictures/pictureOwner';
import { useOpenPictures } from '../pictures/usePicturesNav';
import DirectEdits from './DirectEdits';
import { useSceneThumbs } from './sceneThumbs';
import storyStyles from './StoryStudio.module.scss';
import ownStyles from './SceneStudio.module.scss';

const styles = { ...storyStyles, ...ownStyles } as Record<string, string>;

export const SCENE_STUDIO_TEXT = {
  empty: 'Your scene will appear here. Tell the assistant what happens in it.',
  unavailableHelp: 'You can still play your scene and fix lines and pictures here, or build it by hand under the hood.',
  openHood: 'Open Under the hood',
} as const;

export interface SceneStudioProps {
  sceneId: number;
  /** Switch to Under the hood. */
  onOpenHood: () => void;
}

const NO_MESSAGES: AssistantMessage[] = [];

/** The thumbnail hook with no story listing: it only decodes the strip's pictures. */
const NO_STORY = { storyScenes: [] };

/** The scene as the preview plays it: the scene store's, unsaved edits included. */
function usePreviewScene(sceneId: number): PlayableSceneInput | null {
  const scene = useSceneStore((s) => s.scene);
  const keyframes = useSceneStore((s) => s.keyframes);
  const isLoading = useSceneStore((s) => s.isLoading);
  return useMemo(
    () => previewSceneInput(sceneId) as unknown as PlayableSceneInput | null,
    // scene, keyframes and isLoading are what previewSceneInput reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sceneId, scene, keyframes, isLoading],
  );
}

export const SceneStudio: React.FC<SceneStudioProps> = ({ sceneId, onOpenHood }) => {
  const live = useSceneStore((s) => (Number(s.scene?.id) === sceneId && !s.isLoading ? s.scene : null)) as Story | null;
  const orientation = useSceneStore((s) => s.previewOrientation) as Orientation;
  const setOrientation = useSceneStore((s) => s.setPreviewOrientation);
  const previewScene = usePreviewScene(sceneId);
  // Spec 031 v3: what the author sees here, read at Send: the orientation, and the
  // preview's moment while it is shown (D-16).
  const hostRef = useRef<PlayerHostHandle>(null);
  useAssistantView(true, () => {
    const host = hostRef.current;
    const atMs = momentOf(host?.getFrameState()?.t);
    return {
      surface: 'studio',
      orientation: useSceneStore.getState().previewOrientation as Orientation,
      ...(host && atMs !== undefined ? { atMs, playing: host.isPlaying() } : {}),
    };
  });
  const empty = !live || ((live.sprites?.length ?? 0) === 0 && (live.dialogs?.length ?? 0) === 0);

  // The strip's pictures, decoded once per media id.
  const pictureIds = useMemo(
    () => (live?.sprites ?? []).flatMap((sp) => sp.states.map((st) => st.mediaId).filter((id): id is number => !!id)),
    [live?.sprites],
  );
  const { srcFor } = useSceneThumbs(NO_STORY, pictureIds);

  // ── After the brief: the handoff (D-13, 026 D-10) ────────────────────────
  // While the latest applied turn is the one that answered the pending scene
  // brief and the scene's map lists a picture, until it is closed
  // (remembered per scene for the page session).
  const owner = React.useContext(PictureOwnerContext);
  const sceneMap = owner?.noun === 'scene' ? owner.map : null;
  const messages = useAssistantStore((s) => s.conversations[String(sceneId)] ?? NO_MESSAGES);
  const handoffKey = sceneHandoffKey(sceneId);
  const [handoffClosed, setHandoffClosed] = useState(() => isHandoffClosed(handoffKey));
  useEffect(() => { setHandoffClosed(isHandoffClosed(handoffKey)); }, [handoffKey]);
  const lastApplied = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === 'assistant' && (m.changes?.length ?? 0) > 0) return m;
    }
    return undefined;
  }, [messages]);
  const showHandoff = !handoffClosed && !!lastApplied?.fromBrief && !!sceneMap && pictureMapCounts(sceneMap).total > 0;
  const endHandoff = useCallback(() => {
    closeHandoff(handoffKey);
    setHandoffClosed(true);
  }, [handoffKey]);
  const openPictures = useOpenPictures();

  // ── Keys: undo/redo outside text fields, save anywhere (019's guards) ────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isModalOpen()) return;
      const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().includes('MAC');
      const cmd = isMac ? e.metaKey : e.ctrlKey;
      const key = e.key.toLowerCase();
      const sc = useSceneStore.getState();
      if (cmd && key === 's' && !e.shiftKey) {
        e.preventDefault();
        void sc.saveScene();
        return;
      }
      if (isEditingTarget(e.target)) return;
      const undo = cmd && key === 'z' && !e.shiftKey;
      const redo = (cmd && key === 'z' && e.shiftKey) || (e.ctrlKey && key === 'y');
      if (!undo && !redo) return;
      e.preventDefault();
      if (sc.isPlaying) return;
      if (undo) sc.undo();
      else sc.redo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const hoodButton = (testId: string) => (
    <button type="button" className={styles.primaryButton} onClick={onOpenHood} data-testid={testId}>
      {SCENE_STUDIO_TEXT.openHood}
    </button>
  );

  return (
    <div className={styles.studio} data-testid="scene-studio">
      {/* The chat */}
      <section className={styles.chat} aria-label="Chat">
        <div className={styles.chatBody}>
          <AssistantPanel
            sceneId={sceneId}
            unavailableExtra={(
              <div className={styles.unavailableHelp} data-testid="scene-studio-unavailable-help">
                <p>{SCENE_STUDIO_TEXT.unavailableHelp}</p>
                {hoodButton('scene-studio-unavailable-hood')}
              </div>
            )}
          />
        </div>
      </section>

      {/* The result */}
      <section className={styles.result} aria-label="Preview" tabIndex={-1} data-testid="scene-studio-result">
        <div className={styles.resultBar}>
          <span />
          <OrientationButtons orientation={orientation} onChange={setOrientation} testIdPrefix="scene-studio-orientation" />
        </div>

        {showHandoff && sceneMap ? (
          <PicturesHandoff
            map={sceneMap}
            onFill={() => { endHandoff(); openPictures({ filter: 'needed' }); }}
            onClose={endHandoff}
          />
        ) : empty ? (
          <div className={styles.emptyResult} data-testid="scene-studio-empty">{SCENE_STUDIO_TEXT.empty}</div>
        ) : (
          <StudioPreview sceneId={sceneId} scene={previewScene} orientation={orientation} hostRef={hostRef} />
        )}

        {live && (
          <DirectEdits
            scene={live}
            srcFor={srcFor}
            onOpenHood={onOpenHood}
          />
        )}
      </section>
    </div>
  );
};

type StudioStatus = 'idle' | 'playing' | 'paused' | 'held' | 'ended';

/**
 * The scene's preview, paused on the frame at 0 until Maya plays it (D-14),
 * with 009's `PreviewTransport` under it: ▶/⏸, "⏮ From start", the time and
 * a scrub bar. Held at the choice reveal with the choices clickable (009).
 *
 * - `idle`: a frame shown, no play in progress (on mount, after a scrub from
 *   the end): ▶ plays from the frame shown.
 * - `paused`: a play paused by ⏸ (or a scrub back before a held reveal): ▶ resumes.
 * - `held`: the choice reveal holds: ▶ plays from 0, as 009's single-scene preview.
 * - `ended`: the scene completed: ▶ plays from 0.
 */
const StudioPreview: React.FC<{
  sceneId: number;
  scene: PlayableSceneInput | null;
  orientation: Orientation;
  /** The preview's handle, read by the studio's assistant view (spec 031 v3). */
  hostRef: React.RefObject<PlayerHostHandle>;
}> = ({ sceneId, scene, orientation, hostRef }) => {
  const [status, setStatus] = useState<StudioStatus>('idle');
  const [playhead, setPlayhead] = useState(0);
  const [length, setLength] = useState(0);
  const revealMs = useRef<number | null>(null);
  /** A scrub interrupted a running play: it plays on from the release. */
  const scrubResume = useRef(false);
  const statusRef = useRef(status);
  statusRef.current = status;
  const playheadRef = useRef(playhead);
  playheadRef.current = playhead;
  const hasScene = !!scene;

  // Opening (a new scene, or the player mounting): paused on the frame at 0, no audio
  useEffect(() => {
    if (!hasScene) return;
    setStatus('idle');
    revealMs.current = null;
    hostRef.current?.seek(0);
    setPlayhead(0);
  }, [sceneId, hasScene, hostRef]);

  // The play length is the runtime's; an edit can change it
  useEffect(() => {
    setLength(hostRef.current?.getLength() ?? 0);
  }, [scene, orientation, hostRef]);

  const handlePlay = useCallback(() => {
    const host = hostRef.current;
    if (!host) return;
    const st = statusRef.current;
    if (st === 'paused') host.resume();
    else if (st === 'ended' || st === 'held') host.play(0);
    else host.play(playheadRef.current);
    setLength(host.getLength());
    setStatus('playing');
  }, [hostRef]);

  const handlePause = useCallback(() => {
    hostRef.current?.pause();
    setStatus('paused');
  }, [hostRef]);

  const handleFromStart = useCallback(() => {
    const host = hostRef.current;
    if (!host) return;
    host.play(0);
    setLength(host.getLength());
    setStatus('playing');
  }, [hostRef]);

  const handleScrubStart = useCallback(() => {
    const host = hostRef.current;
    scrubResume.current = statusRef.current === 'playing' && !!host?.isPlaying();
    if (scrubResume.current) host!.pause();
  }, [hostRef]);

  const handleScrub = useCallback((ms: number) => {
    hostRef.current?.seek(ms);
    setPlayhead(ms);
    // Back before a held reveal: the host starts a fresh, paused play there
    if (statusRef.current === 'held' && revealMs.current !== null && ms < revealMs.current) setStatus('paused');
  }, [hostRef]);

  const handleScrubEnd = useCallback(() => {
    if (scrubResume.current) {
      hostRef.current?.resume();
      setStatus('playing');
    } else if (statusRef.current === 'ended') {
      setStatus('idle');
    }
    scrubResume.current = false;
  }, [hostRef]);

  return (
    <>
      <div className={styles.stage} data-testid="scene-studio-preview">
        {scene ? (
          <PlayerHost
            key={sceneId}
            ref={hostRef}
            scene={scene}
            orientation={orientation}
            onPlayhead={setPlayhead}
            onReveal={(ms) => { revealMs.current = ms; setStatus('held'); }}
            onComplete={() => setStatus('ended')}
            className={styles.player}
            data-testid="scene-studio-player"
          />
        ) : (
          <div className={styles.stageLoading}><div className={styles.spinner} /></div>
        )}
      </div>
      {scene && (
        <PreviewTransport
          playing={status === 'playing'}
          playheadMs={playhead}
          lengthMs={length}
          onPlay={handlePlay}
          onPause={handlePause}
          onFromStart={handleFromStart}
          onScrubStart={handleScrubStart}
          onScrub={handleScrub}
          onScrubEnd={handleScrubEnd}
          className={styles.studioTransport}
          data-testid="scene-studio-transport"
        />
      )}
    </>
  );
};

export default SceneStudio;
